import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityId,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  deleteJSON,
  fetchJSON,
  postJSON,
  putJSON,
  putJSONWithoutParsing,
} from 'api/fetch';
import { RootState } from 'initializers/types';

import { showConfirmModal } from 'actions/modals';

import { fetchSchedule } from 'features/scheduleBuilder/ScheduleBuilderView/actions/shared';
import { MODAL_I18N_PATH } from 'features/scheduleBuilder/ScheduleBuilderView/TimeOffPopper/constants';

import { getTimeDifferenceInHours } from 'util/dateTime';
import * as flashNotice from 'util/flashNotice';
import { toI18n } from 'util/i18n';

import {
  captureHydrationException,
  getSliceState,
  selectAttributeForEntity,
} from '../helpers';

import { SLICE_NAME } from './constants';
import {
  CreateTimeOffArgType,
  TimeOff,
  TimeOffRelationshipKeys,
  TimeOffSerializedEntity,
  TimeOffsState,
  UpdateTimeOffResponse,
} from './types';

export const fetchTimeOff = createAsyncThunk(
  `${SLICE_NAME}/fetchTimeOff`,
  async (id: EntityId) => {
    const resp = await fetchJSON(`/schedule_builder/time_offs/${id}`, {
      method: 'GET',
    });
    return resp;
  }
);

export const createTimeOff = createAsyncThunk(
  `${SLICE_NAME}/createTimeOff`,
  (payload: CreateTimeOffArgType) => {
    const processedPayload = { ...payload };

    if (payload.wholeDay) {
      processedPayload.dayDurations = payload.dayDurations.map(dayDuration => ({
        date: dayDuration.date,
        durationInMinutes:
          parseInt(dayDuration.hours || '0', 10) * 60 +
          parseInt(dayDuration.minutes || '0', 10),
      }));
    } else {
      const date = payload.startAt;
      processedPayload.startAt = `${date} ${payload.timeRange?.from}`;
      processedPayload.endAt = `${date} ${payload.timeRange?.to}`;
      processedPayload.dayDurations = [
        {
          date,
          durationInMinutes:
            getTimeDifferenceInHours(
              payload.timeRange?.from || '00:00',
              payload.timeRange?.to || '00:00'
            ) * 60,
        },
      ];
    }

    const transformedPayload = {
      data: {
        type: 'timeOff',
        attributes: {
          category: processedPayload.category,
          startAt: processedPayload.startAt,
          endAt: processedPayload.endAt,
          notes: processedPayload.notes,
          reason: processedPayload.reason,
          wholeDay: processedPayload.wholeDay,
          dayDurations: processedPayload.dayDurations.map(d => ({
            date: d.date,
            durationInMinutes: d.durationInMinutes || 0,
          })),
        },
        relationships: {
          job: {
            data: {
              id: processedPayload.job || '',
              type: 'job',
            },
          },
        },
      },
    };

    return postJSON('/schedule_builder/time_offs', transformedPayload)
      .then(response => {
        if (payload.onSuccess) {
          payload.onSuccess();
        }
        return response.data;
      })
      .catch(() => {
        flashNotice.error(toI18n('errors.could_not_save'));
        throw new Error('Failed to create time off');
      });
  }
);

export const approveTimeOff = createAsyncThunk<
  UpdateTimeOffResponse,
  string,
  { rejectValue: unknown }
>(
  `${SLICE_NAME}/approveTimeOff`,
  async (id: string, { dispatch }) =>
    new Promise<UpdateTimeOffResponse>((resolve, reject) => {
      dispatch(
        showConfirmModal({
          title: toI18n(`${MODAL_I18N_PATH}.approve_time_off`),
          submitText: toI18n(`${MODAL_I18N_PATH}.approve`),
          cancelText: toI18n(`${MODAL_I18N_PATH}.decline`),
          message: toI18n(`${MODAL_I18N_PATH}.message`),
          onConfirm: async () => {
            try {
              const response = await putJSON(
                `/schedule_builder/time_offs/${id}/approve`,
                { data: { id } }
              );
              resolve(response);
            } catch (error) {
              reject(error);
            }
          },
          onCancel: () => {
            reject();
          },
          showCustomIcon: undefined,
          shouldHandleOutsideClick: undefined,
          auto: undefined,
          width: undefined,
          altType: undefined,
          forceOpen: undefined,
          padding: undefined,
          onShow: undefined,
          onClose: undefined,
          fullWidthFooter: undefined,
          onDismiss: undefined,
          newDesign: undefined,
          destructive: undefined,
        })
      );
    })
);

export const declineTimeOff = createAsyncThunk<
  EntityId,
  string,
  { rejectValue: unknown }
>(
  `${SLICE_NAME}/declineTimeOff`,
  async (id: string, { dispatch }) =>
    new Promise<EntityId>((resolve, reject) => {
      dispatch(
        showConfirmModal({
          title: toI18n(`${MODAL_I18N_PATH}.decline_time_off`),
          submitText: toI18n(`${MODAL_I18N_PATH}.approve`),
          cancelText: toI18n(`${MODAL_I18N_PATH}.decline`),
          message: toI18n(`${MODAL_I18N_PATH}.message`),
          onConfirm: async () => {
            try {
              await putJSONWithoutParsing(
                `/schedule_builder/time_offs/${id}/reject`,
                {
                  data: { id },
                }
              );
              resolve(id);
            } catch (error) {
              reject(error);
            }
          },
          onCancel: () => {
            reject();
          },
          showCustomIcon: undefined,
          shouldHandleOutsideClick: undefined,
          auto: undefined,
          width: undefined,
          altType: undefined,
          forceOpen: undefined,
          padding: undefined,
          onShow: undefined,
          onClose: undefined,
          fullWidthFooter: undefined,
          onDismiss: undefined,
          newDesign: undefined,
          destructive: undefined,
        })
      );
    })
);

export const deleteTimeOff = createAsyncThunk<
  { id: string },
  {
    id: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    onDismiss?: () => void;
    onShow?: () => void;
  },
  { rejectValue: unknown }
>(
  `${SLICE_NAME}/deleteTimeOff`,
  async ({ id, onConfirm, onCancel, onDismiss, onShow }, { dispatch }) =>
    new Promise<{ id: string }>((resolve, reject) => {
      dispatch(
        showConfirmModal({
          title: toI18n(`${MODAL_I18N_PATH}.delete_time_off`),
          submitText: toI18n(`${MODAL_I18N_PATH}.approve`),
          cancelText: toI18n(`${MODAL_I18N_PATH}.decline`),
          message: toI18n(`${MODAL_I18N_PATH}.message`),
          onConfirm: async () => {
            try {
              if (onConfirm) {
                onConfirm();
              }

              await deleteJSON(`/schedule_builder/time_offs/${id}`, {
                data: { id },
              });
              resolve({ id });
            } catch (error) {
              reject(error);
            }
          },
          onCancel: () => {
            if (onCancel) {
              onCancel();
            }
            reject();
          },
          onDismiss: () => {
            if (onDismiss) {
              onDismiss();
            }
            reject();
          },
          onShow,
          showCustomIcon: undefined,
          shouldHandleOutsideClick: undefined,
          auto: undefined,
          width: undefined,
          altType: undefined,
          forceOpen: undefined,
          padding: undefined,
          onClose: undefined,
          fullWidthFooter: undefined,
          newDesign: undefined,
          destructive: undefined,
        })
      );
    })
);

export const timeOffsAdapter = createEntityAdapter<TimeOff>();

export const initialState: TimeOffsState = {
  ...timeOffsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

export const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setAll: timeOffsAdapter.setAll,
    removeAll: timeOffsAdapter.removeAll,
    removeOne: timeOffsAdapter.removeOne,
  },
  extraReducers: builder => {
    builder.addCase(fetchSchedule.fulfilled, (state, action) => {
      state.status = 'succeeded';
      if (action.payload?.included) {
        try {
          const timeOffs = action.payload.included
            .filter(
              (
                entity: TimeOffSerializedEntity
              ): entity is TimeOffSerializedEntity => entity.type === 'timeOff'
            )
            .map((entity: TimeOffSerializedEntity) => ({
              id: entity.id,
              type: entity.type,
              unlimited: entity.attributes.unlimited,
              month: entity.attributes.month,
              year: entity.attributes.year,
              startAt: entity.attributes.startAt,
              endAt: entity.attributes.endAt,
              state: entity.attributes.state,
              note: entity.attributes.note,
              notes: entity.attributes.notes,
              isApproved: entity.attributes.isApproved,
              approvalEvent: entity.attributes.approvalEvent,
              isWholeDay: entity.attributes.isWholeDay,
              category: entity.attributes.category,
              reason: entity.attributes.reason,
              createdAt: entity.attributes.createdAt,
              dayDurations: entity.attributes.dayDurations,
              job: entity.relationships?.job?.data,
            }));
          timeOffsAdapter.setAll(state, timeOffs);
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    });

    builder.addCase(fetchTimeOff.fulfilled, (state, action) => {
      state.status = 'succeeded';
      timeOffsAdapter.upsertOne(state, action.payload);
    });

    builder.addCase(fetchTimeOff.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(createTimeOff.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(createTimeOff.fulfilled, (state, action) => {
      state.status = 'succeeded';

      if (action.payload) {
        const timeOffData = action.payload;
        const timeOff: TimeOff = {
          id: timeOffData.id,
          type: timeOffData.type,
          startAt: timeOffData.attributes.startAt,
          endAt: timeOffData.attributes.endAt,
          state: timeOffData.attributes.state,
          note: timeOffData.attributes.note,
          notes: timeOffData.attributes.notes,
          isApproved: timeOffData.attributes.isApproved,
          approvalEvent: timeOffData.attributes.approvalEvent,
          isWholeDay: timeOffData.attributes.isWholeDay,
          category: timeOffData.attributes.category,
          reason: timeOffData.attributes.reason,
          createdAt: timeOffData.attributes.createdAt,
          dayDurations: timeOffData.attributes.dayDurations,
          job: timeOffData.relationships?.job?.data,
        };

        timeOffsAdapter.addOne(state, timeOff);

        if (action.meta?.arg?.onSuccess) {
          action.meta.arg.onSuccess();
        }
      }
    });
    builder.addCase(createTimeOff.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to create time off';
    });
    builder.addCase(approveTimeOff.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(
      approveTimeOff.fulfilled,
      (state, action: PayloadAction<UpdateTimeOffResponse>) => {
        state.status = 'succeeded';
        if (action?.payload?.data) {
          const updatedTimeOff = action.payload.data;
          if (updatedTimeOff.id && updatedTimeOff.attributes) {
            timeOffsAdapter.updateOne(state, {
              id: updatedTimeOff.id,
              changes: {
                state: updatedTimeOff.attributes.state,
                note: updatedTimeOff.attributes.notes,
                startAt: updatedTimeOff.attributes.startAt,
                endAt: updatedTimeOff.attributes.endAt,
                isApproved: updatedTimeOff.attributes.isApproved,
                isWholeDay: updatedTimeOff.attributes.isWholeDay,
                category: updatedTimeOff.attributes.category,
                reason: updatedTimeOff.attributes.reason,
                createdAt: updatedTimeOff.attributes.createdAt,
                dayDurations: updatedTimeOff.attributes.dayDurations,
                job: updatedTimeOff.relationships?.job?.data,
              },
            });
          }
        }
      }
    );
    builder.addCase(approveTimeOff.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to approve time off';
    });
    builder.addCase(declineTimeOff.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(
      declineTimeOff.fulfilled,
      (state, action: PayloadAction<EntityId>) => {
        state.status = 'succeeded';
        timeOffsAdapter.removeOne(state, action.payload);
      }
    );
    builder.addCase(declineTimeOff.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to decline time off';
    });
    builder.addCase(deleteTimeOff.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(deleteTimeOff.fulfilled, (state, action) => {
      state.status = 'succeeded';
      const timeOffId = action.meta.arg?.id || action.payload.id;
      if (timeOffId) {
        timeOffsAdapter.removeOne(state, timeOffId);
      }
    });
    builder.addCase(deleteTimeOff.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to delete time off';
    });
  },
});

export const {
  selectById: selectTimeOffById,
  selectIds: selectTimeOffIds,
  selectEntities: selectTimeOffEntities,
  selectAll: selectAllTimeOffs,
  selectTotal: selectTotalTimeOffs,
} = timeOffsAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

export const { reducer, actions } = slice;

export const selectTimeOffsStatus = (state: RootState) =>
  getSliceState(state)?.[SLICE_NAME]?.status || 'idle';

export const selectTimeOffUpdateIsPending = (state: RootState) =>
  getSliceState(state)?.[SLICE_NAME]?.status === 'pending';

export const selectTimeOffAttribute = <T extends keyof TimeOff>({
  state,
  id,
  attribute,
}: {
  state: RootState;
  id: EntityId;
  attribute: T;
}) =>
  selectAttributeForEntity({
    state,
    attribute,
    entitySelector: selectTimeOffById,
    id,
  });

export const selectTimeOffRelationshipId = ({
  state,
  id,
  relationship,
}: {
  state: RootState;
  id: EntityId;
  relationship: TimeOffRelationshipKeys;
}): EntityId | undefined => {
  const timeOff = selectTimeOffById(state, id);
  if (!timeOff) return undefined;

  if (relationship === 'job') {
    return timeOff.job?.id;
  }

  return undefined;
};

/**
 * example time off response
 * {
    "data": {
        "id": "16",
        "type": "timeOff",
        "attributes": {
            "notes": "",
            "state": "approved",
            "isWholeDay": false,
            "category": "pto_no_policy",
            "reason": null,
            "startAt": "2025-07-09 00:00",
            "endAt": "2025-07-09 00:00",
            "createdAt": "2025-06-23 17:02",
            "isApproved": true,
            "dayDurations": [
                {
                    "date": "2025-07-09",
                    "duration": 0.0
                }
            ]
        },
        "relationships": {
            "job": {
                "data": {
                    "id": "7",
                    "type": "job"
                }
            }
        }
    }
}
 */
